import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader, Plus, Calendar, X, QrCode, Trash2, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { appSettingsService, scannerTokenService, auditService } from '../lib/supabaseClient';
import { useTheme } from '../App';

const KentekenInstellingen = ({ showNotification, user, userProfile }) => {
  const { t } = useTheme();
  const [maxKentekens, setMaxKentekens] = useState('');
  const [evenementDatums, setEvenementDatums] = useState([]);
  const [nieuweDatum, setNieuweDatum] = useState('');
  const [loading, setLoading] = useState(true);
  const [maxKentekensSaving, setMaxKentekensSaving] = useState(false);
  const [datumsSaving, setDatumsSaving] = useState(false);
  const [scannerTokens, setScannerTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [newTokenNaam, setNewTokenNaam] = useState('');
  const [tokenCreating, setTokenCreating] = useState(false);
  const [visibleTokens, setVisibleTokens] = useState(new Set());

  const loadTokens = async () => {
    setTokensLoading(true);
    try {
      const tokens = await scannerTokenService.getAll();
      setScannerTokens(tokens.filter(t => t.active));
    } catch (err) {
      console.error('Error loading scanner tokens:', err);
    } finally {
      setTokensLoading(false);
    }
  };

  const handleCreateToken = async () => {
    if (!newTokenNaam.trim()) return;
    setTokenCreating(true);
    try {
      const token = await scannerTokenService.create(newTokenNaam.trim());
      auditService.log({ changedBy: user?.id, action: 'INSERT', tableName: 'scanner_tokens', recordId: token.id, newData: { naam: token.naam, door: userProfile?.username } });
      setNewTokenNaam('');
      setVisibleTokens(prev => new Set([...prev, token.id]));
      await loadTokens();
      showNotification('Scanner token aangemaakt', 'success');
    } catch (err) {
      showNotification('Fout bij aanmaken token: ' + err.message, 'error');
    } finally {
      setTokenCreating(false);
    }
  };

  const handleRevokeToken = async (tokenId, tokenNaam) => {
    try {
      await scannerTokenService.revoke(tokenId);
      auditService.log({ changedBy: user?.id, action: 'UPDATE', tableName: 'scanner_tokens', recordId: tokenId, newData: { actie: 'ingetrokken', naam: tokenNaam, door: userProfile?.username } });
      await loadTokens();
      showNotification(`Token "${tokenNaam}" ingetrokken`, 'success');
    } catch (err) {
      showNotification('Fout bij intrekken token: ' + err.message, 'error');
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const [maxValue, datumsValue] = await Promise.all([
          appSettingsService.getSetting('max_kentekens_per_dag'),
          appSettingsService.getSetting('evenement_datums')
        ]);
        setMaxKentekens(maxValue || '');
        setEvenementDatums(datumsValue ? JSON.parse(datumsValue) : []);
      } catch (err) {
        console.error('Error loading kenteken settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
    loadTokens();
  }, []);

  const handleSaveMaxKentekens = async () => {
    try {
      setMaxKentekensSaving(true);
      const value = maxKentekens.trim() === '' ? '0' : maxKentekens;
      await appSettingsService.setSetting('max_kentekens_per_dag', value);
      showNotification('Maximum kentekens per dag opgeslagen', 'success');
    } catch (err) {
      console.error('Error saving max kentekens:', err);
      showNotification('Fout bij opslaan: ' + err.message, 'error');
    } finally {
      setMaxKentekensSaving(false);
    }
  };

  const handleAddDatum = async () => {
    if (!nieuweDatum || evenementDatums.includes(nieuweDatum)) return;
    try {
      setDatumsSaving(true);
      const updated = [...evenementDatums, nieuweDatum].sort();
      await appSettingsService.setSetting('evenement_datums', JSON.stringify(updated));
      setEvenementDatums(updated);
      setNieuweDatum('');
      showNotification('Evenement datum toegevoegd', 'success');
    } catch (err) {
      showNotification('Fout bij opslaan: ' + err.message, 'error');
    } finally {
      setDatumsSaving(false);
    }
  };

  const handleRemoveDatum = async (datum) => {
    try {
      setDatumsSaving(true);
      const updated = evenementDatums.filter(d => d !== datum);
      await appSettingsService.setSetting('evenement_datums', JSON.stringify(updated));
      setEvenementDatums(updated);
      showNotification('Evenement datum verwijderd', 'success');
    } catch (err) {
      showNotification('Fout bij opslaan: ' + err.message, 'error');
    } finally {
      setDatumsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-zf-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className={`text-3xl font-bold ${t.text}`}>Kenteken Instellingen</h1>

      {/* Max kentekens per dag */}
      <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Max kentekens per dag</h2>
        <p className={`text-sm ${t.textMuted} mb-4`}>Maximaal aantal kentekens dat per dag toegang heeft (0 = onbeperkt)</p>

        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={maxKentekens}
            onChange={(e) => setMaxKentekens(e.target.value)}
            className={`${t.input} border rounded-lg px-3 py-2 w-24 text-right focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
            placeholder="0"
          />
          <button
            onClick={handleSaveMaxKentekens}
            disabled={maxKentekensSaving}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg hover:bg-zf-accent/80 transition-colors flex items-center gap-2"
          >
            {maxKentekensSaving ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4" />
            )}
            Opslaan
          </button>
        </div>
      </div>

      {/* Evenement datums */}
      <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Evenement datums</h2>
        <p className={`text-sm ${t.textMuted} mb-4`}>Datums waarop het maximum aantal kentekens wordt gecontroleerd</p>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="date"
            value={nieuweDatum}
            onChange={(e) => setNieuweDatum(e.target.value)}
            className={`${t.input} border rounded-lg px-3 py-2 focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
          />
          <button
            onClick={handleAddDatum}
            disabled={datumsSaving || !nieuweDatum}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg hover:bg-zf-accent/80 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {datumsSaving ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Toevoegen
          </button>
        </div>

        {evenementDatums.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {evenementDatums.map(datum => (
              <div
                key={datum}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${t.border} ${t.bgSecondary}`}
              >
                <Calendar className={`w-4 h-4 ${t.textSecondary}`} />
                <span className={`text-sm font-medium ${t.text}`}>
                  {new Date(datum).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                <button
                  onClick={() => handleRemoveDatum(datum)}
                  disabled={datumsSaving}
                  className="text-red-500 hover:text-red-400 ml-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className={`text-sm ${t.textMuted} italic`}>Geen evenement datums ingesteld</p>
        )}
      </div>

      {/* Scanner App Tokens */}
      <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className={`text-xl font-semibold ${t.text}`}>Scanner App Tokens</h2>
            <p className={`text-sm ${t.textMuted} mt-1`}>QR-codes voor de mobiele kentekenscanner app (geen MFA vereist)</p>
          </div>
          <button onClick={loadTokens} className={`${t.textMuted} hover:text-zf-accent transition-colors`} title="Verversen">
            <RefreshCw size={16} className={tokensLoading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <input
            type="text"
            value={newTokenNaam}
            onChange={(e) => setNewTokenNaam(e.target.value)}
            placeholder="Naam scanner (bijv. Scanner Ingang)"
            className={`flex-1 ${t.input} border rounded-lg px-3 py-2 focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
          />
          <button
            onClick={handleCreateToken}
            disabled={tokenCreating || !newTokenNaam.trim()}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg hover:bg-zf-accent/80 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {tokenCreating ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Aanmaken
          </button>
        </div>

        {scannerTokens.length === 0 ? (
          <p className={`text-sm ${t.textMuted} italic`}>Geen actieve scanner tokens</p>
        ) : (
          <div className="space-y-4">
            {scannerTokens.map(token => {
              const scannerUrl = `${window.location.origin}/scanner?token=${token.token}`;
              const isVisible = visibleTokens.has(token.id);
              return (
                <div key={token.id} className={`${t.bgSecondary} border ${t.border} rounded-lg p-4`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold ${t.text}`}>{token.naam}</p>
                      <p className={`text-xs ${t.textMuted} mt-1`}>
                        Aangemaakt: {new Date(token.created_at).toLocaleDateString('nl-NL')} &nbsp;·&nbsp;
                        Geldig tot: {new Date(token.expires_at).toLocaleDateString('nl-NL')}
                      </p>
                      {token.last_used_at && (
                        <p className={`text-xs ${t.textMuted}`}>
                          Laatste gebruik: {new Date(token.last_used_at).toLocaleString('nl-NL')}
                          {token.last_used_ip && ` (${token.last_used_ip})`}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setVisibleTokens(prev => {
                          const next = new Set(prev);
                          isVisible ? next.delete(token.id) : next.add(token.id);
                          return next;
                        })}
                        className={`flex items-center gap-1 text-xs px-3 py-1.5 border ${t.border} rounded-lg ${t.hover} ${t.textMuted} transition-colors`}
                      >
                        <QrCode size={14} />
                        {isVisible ? 'Verberg' : 'QR-code'}
                      </button>
                      <button
                        onClick={() => handleRevokeToken(token.id, token.naam)}
                        className="text-red-400 hover:text-red-300 p-1.5"
                        title="Token intrekken"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {isVisible && (
                    <div className="mt-4 flex flex-col items-center gap-3">
                      <div className="bg-white p-4 rounded-lg">
                        <QRCodeSVG value={scannerUrl} size={180} />
                      </div>
                      <p className={`text-xs ${t.textMuted} text-center break-all max-w-sm`}>{scannerUrl}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default KentekenInstellingen;
